import React from "react";
import {
  Parah,
  PointsIcon,
  PointsWrapper,
  SubTitle,
  ThirdTitle,
} from "../ContentsStyles";

const PP_4 = () => {
  return (
    <>
      <Parah>
        We may only use your information when we have a “legal basis” to do so.
        We use different legal bases depending on why we use your information
        (in other words, the “purpose” of our processing). These legal bases are
        contractual necessity, legitimate interests (ours, yours or those of
        another party), consent, compliance with a legal obligation, performing
        a task in the public interest, and protection of vital interests.
      </Parah>
      <Parah>
        Here we explain the legal bases we rely on when we process your
        information. This section also describes why we use your information,
        how this information is processed, the categories of information
        involved, and the associated rights (which always includes the right to
        access your information). For more information on how to exercise any of
        your rights, please check out the “Your Rights and Choices” section in
        the Privacy Policy.
      </Parah>
      <SubTitle>Contractual Necessity</SubTitle>
      <Parah>
        We use Information You Provide, Automatically Collected Information, and
        Information From Other Sources where it is necessary to perform the
        contract you enter into (our Terms) when you register for, access, or
        use the Platform. This means we use your information to:
      </Parah>
      <PointsWrapper>
        <PointsIcon />
        Provide you with the Platform
      </PointsWrapper>
      <Parah>
        We use your information to allow you to access and use the Platform,
        including allowing you to create and consume content, interact with
        others, utilise social features, and provide you with products and
        services you purchase. We also personalise your experience on the
        Platform, such as providing your “For You” feed, which shows you videos
        that we think you would be interested in and by suggesting accounts to
        you.
      </Parah>
      <PointsWrapper>
        <PointsIcon />
        Enforce our Terms, Guidelines, or policies
      </PointsWrapper>
      <Parah>
        We use your information to enforce our Terms, Guidelines, and policies.
        This may include removing content (including through automated means) or
        suspending or banning your account if we determine that you are
        violating our Terms or Guidelines.
      </Parah>
      <PointsWrapper>
        <PointsIcon />
        Administer the Platform
      </PointsWrapper>
      <Parah>
        We also use the information we have to administer the Platform, such as
        to communicate with you on service-related matters and respond to your
        queries (including when you tell us about a problem or ask for a copy of
        your information).
      </Parah>
      <Parah>
        Your Rights: Whenever we use your information on the basis that it is
        necessary for the contract we have with you, you have the right to port
        information you have provided to us.
      </Parah>
      <SubTitle>Legitimate Interests</SubTitle>
      <Parah>
        We use your information where this is necessary to achieve legitimate
        interests - whether belonging to us, you, or a third party - provided
        these interests are not outweighed by your interests or fundamental
        rights and freedoms. We use the information we collect to:
      </Parah>
      <ThirdTitle>
        Enable users to create content with interactive features.
      </ThirdTitle>
      <PointsWrapper>
        <PointsIcon />
        Legitimate Interest relied upon - To provide users with tools to inspire
        creativity, collaboration, and enjoyment, and to create opportunities
        for users to reach new audiences.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Information used - Information You Provide and Automatically Collected
        Information.
      </PointsWrapper>
      <ThirdTitle>Suggest your account to other users</ThirdTitle>
      <PointsWrapper>
        <PointsIcon />
        We may suggest your account to other users, including when a user
        chooses to sync their contacts with the Platform.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Legitimate Interest relied upon - To enable users to quickly and
        efficiently find and connect with other users.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Information used - Information You Provide, Automatically Collected
        Information, and Information From Other Sources.
      </PointsWrapper>
      <ThirdTitle>
        Provide all users with non-personalised advertising
      </ThirdTitle>
      <PointsWrapper>
        <PointsIcon />
        We show all users non-personalised advertising, including those who have
        chosen not to receive personalised ads.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Legitimate Interest relied upon - We show non-personalised ads to keep
        the Platform free of charge. We process limited categories of your
        information to make sure our advertising service is useful to our
        advertisers (for example, to serve ads to users in a particular country
        or to users who speak the same language).
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Information used - Automatically and Information From Other Sources.
      </PointsWrapper>
      <ThirdTitle>Provide measurement and analytics services</ThirdTitle>
      <PointsWrapper>
        <PointsIcon />
        We help creators and advertisers measure the effectiveness and
        distribution of ads and other content viewed on the Platform.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Legitimate Interest relied upon - To enable creators and advertisers to
        view and understand how their ads or content performed and the audience
        that viewed or interacted with their ads or content. This is so that
        they can make informed decisions (such as what kinds of ad campaigns
        they may want to run, the type of audience they want to reach and the
        types of content that users enjoy the most). This helps keep the
        advertising and other content we show users relevant and entertaining,
        and keeps the Platform free for all users.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Information used - Information used: Information You Provide,
        Automatically Collected Information, and Information From Other Sources.
      </PointsWrapper>
      <ThirdTitle>Ensure the safety and well-being of our community</ThirdTitle>
      <PointsWrapper>
        <PointsIcon />
        We use your information to ensure the safety and well-being of our
        community, including by reviewing User Content (and if permitted under
        applicable law, Direct Messages and associated metadata) for breaches of
        our Terms, Guidelines, and other policies. We also infer your age range,
        and under certain circumstances, may ask you to provide additional
        information to confirm your age.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Legitimate Interest relied upon - To keep our community safe, review
        compliance with our Guidelines, and identify misuse of the Platform.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Information used - Information You Provide, Automatically Collected
        Information, and Information From Other Sources.
      </PointsWrapper>
      <ThirdTitle>Ensure the security and stability of the Platform</ThirdTitle>
      <PointsWrapper>
        <PointsIcon />
        We use your information to ensure the stability and security of the
        Platform, including by identifying and combating technical or security
        issues (such as technical bugs, spam accounts, and detecting abuse,
        fraud, and illegal activity).
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Legitimate Interest relied upon - To ensure the ongoing stability and
        security of the Platform and to ensure it is continuously available and
        functioning.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Information used - Information You Provide, Automatically Collected
        Information, and Information From Other Sources.
      </PointsWrapper>
      <ThirdTitle>Review, improve, promote and develop the Platform</ThirdTitle>
      <PointsWrapper>
        <PointsIcon />
        We use the information we have to understand how people are using the
        Platform and to develop and improve the Platform, including to train,
        develop, and improve our technology such as our machine learning models
        and algorithms. We also use this information to better understand and
        promote the Platform, popular topics, and emerging trends.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Legitimate Interest relied upon - To improve, promote, and develop the
        Platform in an informed way.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Information used - Information You Provide, Automatically Collected
        Information, and Information From Other Sources.
      </PointsWrapper>
      <ThirdTitle>Share your information with third parties</ThirdTitle>
      <PointsWrapper>
        <PointsIcon />
        We share your information with third parties whose platforms or services
        are integrated with the Platform.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Legitimate Interest relied upon: To provide you with a seamless
        experience, allow your content to be shared on other platforms, enable
        third parties to authenticate users, and optimise user experience.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Information used - Information You Provide, Automatically Collected
        Information, and Information From Other Sources.
      </PointsWrapper>
      <ThirdTitle>Marketing communications</ThirdTitle>
      <PointsWrapper>
        <PointsIcon />
        We may send you direct marketing messages about features of the Platform
        and third party services that we think will interest you. We may also
        send you promotions, contests, or marketing campaigns or event
        information. We will obtain your consent to send you messages where
        required by law.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Legitimate Interest relied upon - To promote the Platform or third party
        products and services.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Information used - Information You Provide, Automatically Collected
        Information, and Information From Other Sources.
      </PointsWrapper>
      <Parah>
        Your Rights: Whenever we use your information on the basis that it is
        necessary for legitimate interests, you can object to, and request
        restriction of, such usage.
      </Parah>
      <SubTitle>Your Consent</SubTitle>
      <Parah>
        We ask for your consent to access or use your information for specific
        purposes. If we do, you’ll always be able to revoke your consent through
        your device permissions or in-app settings.
      </Parah>
      <ThirdTitle>Personalised advertising</ThirdTitle>
      <PointsWrapper>
        <PointsIcon />
        With your consent, we will serve you with personalised ads based on your
        activity on and off the Platform.
      </PointsWrapper>
      <PointsWrapper>
        <PointsIcon />
        Information used: Information You Provide, Automatically Collected
        Information, and Information From Other Sources for this purpose.
      </PointsWrapper>
      <Parah>
        Your rights: Whenever we use your information based on your consent, you
        can withdraw your consent at any time. However, your withdrawal of
        consent will not affect the lawfulness of processing your information
        based on your consent prior to your withdrawal of consent. You can
        withdraw your consent for applicable personalised ads in settings.
      </Parah>
      <Parah>
        You also have the right to port information you have provided to us that
        we use based on your consent.
      </Parah>
      <SubTitle>Compliance with a Legal Obligation</SubTitle>
      <Parah>
        We may use your information including your Profile Information or User
        Content where it is necessary to comply with a legal obligation. This
        includes situations where we have obligations to take measures to ensure
        the safety of our users or comply with a valid legal request such as an
        order from law enforcement agencies or courts. We generally use
        Information You Provide and Automatically Collected Information,
        although it depends on the specific situation.
      </Parah>
      <SubTitle>To Perform a Task in the Public Interest</SubTitle>
      <Parah>
        We may use your information where it is necessary to perform a task in
        the public interest, including undertaking research, preventing, and
        detecting crime, safeguarding children and promoting public safety,
        security, and integrity as laid down by applicable law. We generally use
        Information You Provide and Automatically Collected Information,
        although it depends on the specific situation.
      </Parah>
      <Parah>
        Your rights: When we use your information on the basis that it is
        necessary for a task carried out in the public interest, you have the
        right to object to, and seek restriction of, our usage.
      </Parah>
      <SubTitle>To Protect Someone's Vital Interests</SubTitle>
      <Parah>
        We may use your information where it is necessary to protect your or
        someone else's life, physical integrity, or safety. This could include
        providing law enforcement agencies or emergency services with
        information in urgent situations to protect health or life. We generally
        provide Information You Provide and Automatically Collected Information,
        although it depends on the specific situation.
      </Parah>
    </>
  );
};

export default PP_4;
